import { useEffect, useRef, type Dispatch, type SetStateAction } from 'react';
import { apiGet, operationsClient } from '@/lib/api';
import type { Estimate } from '@/types/estimate';
import {
  deriveEstimateFormValuesFromEstimate,
  deriveExistingEstimateAddressState,
  formatEstimateAddress,
  type EstimateContactAddress,
} from '@/routes/_dashboard/dashboard/estimates/_shared';

interface UseEstimateEditHydrationArgs {
  isEditMode: boolean;
  existingEstimate?: Estimate;
  applyDraftState: (next: ReturnType<typeof deriveEstimateFormValuesFromEstimate>) => void;
  applyAddress: (addr: {
    street?: string;
    street2?: string | null;
    city?: string;
    state?: string;
    zip?: string;
  }) => void;
  setContactId: Dispatch<SetStateAction<string | null>>;
  setBillToId: Dispatch<SetStateAction<string | null | undefined>>;
  setContactAddresses: Dispatch<SetStateAction<EstimateContactAddress[]>>;
  setAddressMode: Dispatch<SetStateAction<'select' | 'manual'>>;
  setSelectedAddressIndex: Dispatch<SetStateAction<number | null>>;
  setSaStreet: Dispatch<SetStateAction<string>>;
  setSaStreet2: Dispatch<SetStateAction<string>>;
  setSaCity: Dispatch<SetStateAction<string>>;
  setSaState: Dispatch<SetStateAction<string>>;
  setSaZip: Dispatch<SetStateAction<string>>;
  setBillingMode: Dispatch<SetStateAction<'select' | 'manual'>>;
  setSelectedBillingIndex: Dispatch<SetStateAction<number | null>>;
  setCustomerAddress: Dispatch<SetStateAction<string>>;
  setBaStreet: Dispatch<SetStateAction<string>>;
  setBaStreet2: Dispatch<SetStateAction<string>>;
  setBaCity: Dispatch<SetStateAction<string>>;
  setBaState: Dispatch<SetStateAction<string>>;
  setBaZip: Dispatch<SetStateAction<string>>;
}

export function useEstimateEditHydration({
  isEditMode,
  existingEstimate,
  applyDraftState,
  applyAddress,
  setContactId,
  setBillToId,
  setContactAddresses,
  setAddressMode,
  setSelectedAddressIndex,
  setSaStreet,
  setSaStreet2,
  setSaCity,
  setSaState,
  setSaZip,
  setBillingMode,
  setSelectedBillingIndex,
  setCustomerAddress,
  setBaStreet,
  setBaStreet2,
  setBaCity,
  setBaState,
  setBaZip,
}: UseEstimateEditHydrationArgs) {
  const didPopulateRef = useRef(false);

  useEffect(() => {
    if (!isEditMode || !existingEstimate || didPopulateRef.current) return;

    didPopulateRef.current = true;
    const estimate = existingEstimate;

    applyDraftState(deriveEstimateFormValuesFromEstimate(estimate));

    const applyManualServiceAddress = (serviceAddress?: Estimate['serviceAddress'] | null) => {
      if (!serviceAddress) return;
      setSaStreet(serviceAddress.street ?? '');
      setSaStreet2(serviceAddress.street2 ?? '');
      setSaCity(serviceAddress.city ?? '');
      setSaState(serviceAddress.state ?? '');
      setSaZip(serviceAddress.zip ?? '');
      setAddressMode('manual');
    };

    const parseLegacyBillingAddress = (address?: string | null): Estimate['billingAddress'] => {
      if (!address) return null;
      const parts = address
        .split(',')
        .map((part) => part.trim())
        .filter(Boolean);
      if (parts.length < 4) return null;

      const zip = parts[parts.length - 1] ?? '';
      const state = parts[parts.length - 2] ?? '';
      const city = parts[parts.length - 3] ?? '';
      const streetParts = parts.slice(0, -3);

      return {
        street: streetParts[0] ?? '',
        ...(streetParts.length > 1 && { street2: streetParts.slice(1).join(', ') }),
        city,
        state,
        zip,
      };
    };

    const applyManualBillingAddress = () => {
      const billingAddress =
        estimate.billingAddress ?? parseLegacyBillingAddress(estimate.customer?.address);

      if (!billingAddress) {
        setBillingMode('select');
        setSelectedBillingIndex(null);
        setCustomerAddress(estimate.customer?.address ?? '');
        setBaStreet('');
        setBaStreet2('');
        setBaCity('');
        setBaState('');
        setBaZip('');
        return;
      }

      setBillingMode('manual');
      setSelectedBillingIndex(null);
      setCustomerAddress(formatEstimateAddress(billingAddress));
      setBaStreet(billingAddress.street ?? '');
      setBaStreet2(billingAddress.street2 ?? '');
      setBaCity(billingAddress.city ?? '');
      setBaState(billingAddress.state ?? '');
      setBaZip(billingAddress.zip ?? '');
    };

    const contactId = estimate.contactId;
    if (!contactId) {
      setContactAddresses([]);
      setSelectedAddressIndex(null);
      applyManualServiceAddress(estimate.serviceAddress);
      applyManualBillingAddress();
      return;
    }

    setContactId(contactId);
    setBillToId((estimate as { billToId?: string | null }).billToId ?? null);
    apiGet<Record<string, unknown>>(operationsClient, `/contacts/${contactId}`)
      .then((contact) => {
        const addresses = (contact.addresses ?? []) as EstimateContactAddress[];
        const next = deriveExistingEstimateAddressState({
          addresses,
          serviceAddress: estimate.serviceAddress ?? undefined,
          billingAddress: estimate.customer?.address ?? '',
          billingAddressStructured: estimate.billingAddress ?? undefined,
        });

        setContactAddresses(addresses);
        setAddressMode(next.address.addressMode);
        setSelectedAddressIndex(next.address.selectedAddressIndex);

        if (next.address.serviceAddress) {
          applyAddress(next.address.serviceAddress);
        } else {
          setSaStreet('');
          setSaStreet2('');
          setSaCity('');
          setSaState('');
          setSaZip('');
        }

        setBillingMode(next.billing.billingMode);
        setSelectedBillingIndex(next.billing.selectedBillingIndex);
        setCustomerAddress(next.billing.customerAddress);

        // Seeded in select mode too, not just manual. buildEstimatePayload
        // reads ba* only when billingMode is 'manual' — in select mode the
        // address is resolved from the index — so carrying the structure here
        // is inert for the payload and is the only thing that lets a later
        // switch to manual entry reproduce the address the user was shown.
        // Six of findPreferredBillingSelection's branches return a null index
        // with a populated customerAddress, and a display string cannot be
        // turned back into fields.
        if (next.billing.manualBillingAddress) {
          const ba = next.billing.manualBillingAddress;
          setBaStreet(ba.street ?? '');
          setBaStreet2(ba.street2 ?? '');
          setBaCity(ba.city ?? '');
          setBaState(ba.state ?? '');
          setBaZip(ba.zip ?? '');
        } else {
          setBaStreet('');
          setBaStreet2('');
          setBaCity('');
          setBaState('');
          setBaZip('');
        }
      })
      .catch(() => {
        setContactAddresses([]);
        setSelectedAddressIndex(null);
        setSelectedBillingIndex(null);
        applyManualServiceAddress(estimate.serviceAddress);
        applyManualBillingAddress();
      });
  }, [
    isEditMode,
    existingEstimate,
    applyDraftState,
    applyAddress,
    setContactId,
    setBillToId,
    setContactAddresses,
    setAddressMode,
    setSelectedAddressIndex,
    setSaStreet,
    setSaStreet2,
    setSaCity,
    setSaState,
    setSaZip,
    setBillingMode,
    setSelectedBillingIndex,
    setCustomerAddress,
    setBaStreet,
    setBaStreet2,
    setBaCity,
    setBaState,
    setBaZip,
  ]);

  return {
    /**
     * Allow a server-side reprice (e.g. the tax-group pin) to re-apply the
     * freshly fetched estimate onto the form: clears the once-only guard so
     * the hydration effect runs again when the refetched row lands.
     */
    rehydrate: () => {
      didPopulateRef.current = false;
    },
  };
}
